/**
 * Route prefetching utilities for faster navigation
 */

const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's assets by creating a link element
 */
export const prefetchRoute = (route: string) => {
  if (prefetchedRoutes.has(route)) return;
  
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = route;
  link.as = 'fetch';
  
  document.head.appendChild(link);
  prefetchedRoutes.add(route);
};

/**
 * Prefetch multiple routes at once
 */
export const prefetchRoutes = (routes: string[]) => {
  routes.forEach(route => prefetchRoute(route));
};

/**
 * Prefetch images that will likely be needed soon
 */
export const prefetchImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Prefetch multiple images
 */
export const prefetchImages = (srcs: string[]) => {
  return Promise.allSettled(srcs.map(src => prefetchImage(src)));
};

/**
 * Preload a JavaScript module
 */
export const preloadModule = (modulePath: string) => {
  const link = document.createElement('link');
  link.rel = 'modulepreload';
  link.href = modulePath;
  document.head.appendChild(link);
};

/**
 * Auto-prefetch routes based on user behavior
 */
export const setupSmartPrefetch = () => {
  let prefetchTimeout: NodeJS.Timeout;
  
  // Prefetch on hover (desktop)
  document.addEventListener('mouseover', (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    
    if (link && link.href && !link.href.includes('#')) {
      clearTimeout(prefetchTimeout);
      prefetchTimeout = setTimeout(() => {
        prefetchRoute(link.href);
      }, 100); // Small delay to avoid prefetching on quick mouse movements
    }
  }, { passive: true });
  
  // Prefetch on touch start (mobile)
  document.addEventListener('touchstart', (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    
    if (link && link.href && !link.href.includes('#')) {
      prefetchRoute(link.href);
    }
  }, { passive: true });
};

/**
 * Priority hint for resource loading
 */
export const setResourcePriority = (element: HTMLElement, priority: 'high' | 'low' | 'auto') => {
  if ('fetchPriority' in element) {
    (element as any).fetchPriority = priority;
  }
};
